<template>
  <div class="q-pa-md">
    <q-table
      :rows="complexes"
      :columns="complexColumns"
      row-key="complexId"
      :visible-columns="visibleComplexColumns"
      v-model:pagination="pagination"
      :rows-per-page-options="[5, 10, 25, 50]"
      @request="onRequest"
      no-data-label="No complexes exist, please create a new complex"
      binary-state-sort
    >
      <template v-slot:top-left>
        <div
          v-if="retired"
          class="text-h6"
        >
          Complexes - Showing Retired Rows
        </div>
        <div
          v-else
          class="text-h6"
        >
          Complexes
        </div>
      </template>
      <template v-slot:top-right>
        <q-select
          class="q-mr-xs"
          v-model="visibleComplexColumns"
          multiple
          outlined
          dense
          options-dense
          display-value="Complex"
          emit-value
          map-options
          :options="complexColumns"
          option-value="name"
          options-cover
        >
          <template #option="scope">
            <q-item
              clickable
              @click="scope.toggleOption(scope.opt)"
            >
              <q-item-section side>
                <q-checkbox
                  :model-value="scope.selected"
                  @click="scope.toggleOption(scope.opt)"
                />
              </q-item-section>

              <q-item-section>
                {{ scope.opt.name }}
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-select
          class="q-mr-xs"
          v-model="visibleUnitColumns"
          multiple
          outlined
          dense
          options-dense
          display-value="Unit"
          emit-value
          map-options
          :options="unitColumns"
          option-value="name"
          options-cover
        >
          <template #option="scope">
            <q-item
              clickable
              @click="scope.toggleOption(scope.opt)"
            >
              <q-item-section side>
                <q-checkbox
                  :model-value="scope.selected"
                  @click="scope.toggleOption(scope.opt)"
                />
              </q-item-section>

              <q-item-section>
                {{ scope.opt.name }}
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-btn
          v-if="isShowingRetired"
          class="q-mr-xs"
          color="grey"
          icon="history_toggle_off"
          no-caps
          @click="hideRetired"
        />
        <q-btn
          v-else
          class="q-mr-xs"
          color="primary"
          icon-right="restore"
          no-caps
          @click="showRetired"
        />
        <q-btn
          v-if="!isRa"
          icon-right="add"
          class="q-mr-xs"
          color="primary"
          @click="isAddComplexModal = true"
          no-caps
        />
        <q-dialog
          ref="complexEditModal"
          v-model="isAddComplexModal"
        >
          <q-card style="width: 700px; max-width: 80vw;">
            <SubmitComplex ref="complexForm" />
            <q-card-actions
              align="center"
              class="bg-white text-teal"
            >
              <q-btn
                flat
                :label="isRa ? 'Close' : 'Cancel'"
                v-close-popup
                color="primary"
              />
              <q-btn
                v-if="!isRa"
                flat
                label="Save"
                @click="submitComplexForm()"
              />
            </q-card-actions>
          </q-card>
        </q-dialog>
        <q-btn
          class="q-mr-xs"
          color="primary"
          icon-right="archive"
          no-caps
          @click="exportUnits"
        />
      </template>
      <template v-slot:header="props">
        <q-tr :props="props">
          <q-th>
            Show Units
          </q-th>
          <q-th
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
            class="text-left"
          >
            {{ col.label }}
          </q-th>
          <q-th auto-width>
            Add Unit
          </q-th>
          <q-th
            auto-width
            v-if="hasRoles('R&D', 'HRL')"
          >
            Edit
          </q-th>
          <q-th v-else>
            View
          </q-th>
          <q-th auto-width>
            Retire/Unretire
          </q-th>
        </q-tr>
      </template>

      <template v-slot:body="props">
        <q-tr
          :props="props"
          :style="props.row.retired === false ? '' : 'background-color: #f1f1f1;'"
        >
          <q-td auto-width>
            <q-toggle
              @click="fetchUnitsByComplexId(props)"
              v-model="props.row.unitToggle"
              checked-icon="add"
              unchecked-icon="remove"
              color="positive"
            />
          </q-td>
          <q-td
            auto-width
            v-for="
              col
              in
              props.cols"
            :key="col.name"
            :props="props"
            class="text-left"
          >
            {{ col.value }}
          </q-td>
          <q-td auto-width>
            <q-btn
              @click="resetCurrentUnit();
                      isAddUnitModal = true;
                      updateComplexState(props.row.complexId)"
              size="sm"
              color="white"
              text-color="primary"
              round
              flat
              icon="add"
            />
          </q-td>
          <q-td>
            <q-btn
              @click="isAddComplexModal = true; updateComplexState(props.row.complexId)"
              size="sm"
              color="white"
              text-color="primary"
              round
              flat
              :icon="hasRoles('R&D', 'HRL') ? 'edit' : 'visibility'"
            />
          </q-td>
          <q-td>
            <q-btn
              v-if="props.row.retired"
              @click="unRetireRow(props.row.complexId, 'Complexes')"
              class="q-mr-xs"
              size="sm"
              color="white"
              text-color="grey"
              round
              flat
              icon="restore"
              no-caps
            />
            <q-btn
              v-else
              @click="updateComplexState(props.row.complexId), confirmRetire = true"
              class="q-mr-xs"
              size="sm"
              color="white"
              text-color="primary"
              round
              flat
              icon="unpublished"
              no-caps
            />
          </q-td>
        </q-tr>
        <q-tr
          v-show="props.row.unitToggle"
          :props="props"
        >
          <q-td colspan="100%">
            <q-table
              :rows="props.row.unitModels"
              :columns="unitColumns"
              :visible-columns="visibleUnitColumns"
              row-key="unitId"
              no-data-label="No units exist under this complex, please create a new unit"
              binary-state-sort
            >
              <template v-slot:header="props">
                <q-tr
                  style="background-color: #9FE3DA;"
                  :props="props"
                >
                  <q-th auto-width />
                  <q-th
                    v-for="col in props.cols"
                    :key="col.name"
                    :props="props"
                    class="text-left"
                    auto-width
                  >
                    {{ col.label }}
                  </q-th>
                  <q-th auto-width>
                    Edit
                  </q-th>
                  <q-th auto-width>
                    Retire/Unretire
                  </q-th>
                </q-tr>
              </template>

              <template v-slot:body="props">
                <q-tr
                  :props="props"
                  :style="props.row.retired === false ? '' : 'background-color: #f1f1f1;'"
                >
                  <q-td auto-width />
                  <q-td
                    v-for="unit in props.cols"
                    :key="unit.name"
                    :props="props"
                    auto-width
                    class="text-left"
                  >
                    <div v-if="unit.field === 'dssAccessible'">
                      {{ $filters.yesNo(unit.value) }}
                    </div>
                    <div v-else>
                      {{ unit.value }}
                    </div>
                  </q-td>
                  <q-td auto-width>
                    <q-btn
                      @click="isAddUnitModal = true; updateUnitState(props.row.unitId)"
                      size="sm"
                      color="white"
                      text-color="primary"
                      round
                      flat
                      icon="edit"
                    />
                  </q-td>
                  <q-td>
                    <q-btn
                      v-if="props.row.retired"
                      @click="unRetireRow(props.row.unitId, 'Units')"
                      class="q-mr-xs"
                      size="sm"
                      color="white"
                      text-color="grey"
                      round
                      flat
                      icon="restore"
                      no-caps
                    />
                    <q-btn
                      v-else
                      @click="retireRow(props.row.unitId, 'Units')"
                      class="q-mr-xs"
                      size="sm"
                      color="white"
                      text-color="primary"
                      round
                      flat
                      icon="unpublished"
                      no-caps
                    />
                  </q-td>
                </q-tr>
              </template>
            </q-table>

          </q-td>
        </q-tr>
      </template>
    </q-table>
    <q-dialog
      v-model="confirmRetire"
      persistent
    >
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar
            icon="info"
            size="md"
            color="primary"
            text-color="white"
          />
          <span class="q-ml-sm">
            Units within this Complex will also be retired.
            <br />
            Are you sure you want to continue?
          </span>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="Cancel"
            v-close-popup
          />
          <q-btn
            flat
            color="primary"
            label="Confirm"
            @click="retireRow(currentComplexId, 'Complexes')"
            v-close-popup
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog
      ref="unitEditModal"
      v-model="isAddUnitModal"
    >
      <q-card style="width: 700px; max-width: 80vw;">
        <SubmitUnit ref="unitForm" />

        <q-card-actions
          align="center"
          class="bg-white text-teal"
        >
          <q-btn
            flat
            label="Cancel"
            v-close-popup
            color="primary"
          />
          <q-btn
            flat
            label="Save"
            @click="submitUnitForm()"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import { useRoute } from 'vue-router';
import axios from 'axios';
import useAuth from '@/hooks/auth';
import useComplex from '@/hooks/complex';
import useUnit from '@/hooks/unit';
import useUiUtilities from '@/utilities/uiUtilities';
import SubmitComplex from './SubmitComplex.vue';
import SubmitUnit from '../Units/SubmitUnit.vue';

export default {
  async setup() {
    const $q = useQuasar();
    const route = useRoute();
    const { hasRoles } = useAuth();
    const {
      complexDetails,
      currentComplexId,
      fetchComplex,
    } = useComplex();
    const {
      currentUnitId,
      fetchUnit,
      getUnitsExport,
      resetCurrentUnit,
      unitDetails,
    } = useUnit();
    const { createDownloadableFile, notifyResult } = useUiUtilities();

    let { retired } = route.query;
    retired = retired === 'true';
    const complexForm = ref();
    const unitForm = ref();
    const complexEditModal = ref();
    const unitEditModal = ref();
    const isShowingRetired = ref(false);
    const isRa = !hasRoles('HRL', 'R&D') && hasRoles('RESIDENT-ASSISTANT');

    const unitColumns = [
      {
        name: 'Unit Name', required: true, align: 'left', label: 'Unit Name', field: (row) => row.name, sortable: true,
      },
      {
        name: 'Room Rate Class',
        align: 'left',
        label: 'Room Rate Class',
        field: (row) => row.roomRateClass,
        sortable: true,
      },
      {
        name: 'Capacity', align: 'right', label: 'Capacity', field: (row) => row.capacity, sortable: true,
      },
      {
        name: 'DSS Accessible', align: 'left', label: 'DSS Accessible', field: 'dssAccessible', sortable: true,
      },
      {
        name: 'WiFi Name', align: 'left', label: 'WiFi Name', field: (row) => row.wiFiName,
      },
      {
        name: 'WiFi Password', align: 'left', label: 'WiFi Password', field: (row) => row.wiFiPassword,
      },
      {
        name: 'Notes',
        label: 'Notes',
        align: 'left',
        field: (row) => row.notes,
        classes: 'ellipsis',
        style: 'max-width: 200px',
        headerStyle: 'max-width: 200px',
      },
    ];

    const complexColumns = [
      {
        name: 'Complex Name', align: 'left', label: 'Complex Name', field: (row) => row.name, sortable: true,
      },
      {
        name: 'Address Name', align: 'left', label: 'Street Name', field: (row) => row.addressName,
      },
      {
        name: 'City Name', align: 'left', label: 'City Name', field: (row) => row.cityName,
      },
      {
        name: 'State Code', align: 'left', label: 'State Code', field: (row) => row.stateCode,
      },
      {
        name: 'Zip Code', align: 'right', label: 'Zip Code', field: (row) => row.zipCode,
      },
      {
        name: 'Contact Name', align: 'left', label: 'Contact Name', field: (row) => row.contactName, sortable: true,
      },
      {
        name: 'Contact Phone', align: 'right', label: 'Contact Phone', field: (row) => row.contactPhone, sortable: true,
      },
      {
        name: 'Contact Email', align: 'left', label: 'Contact Email', field: (row) => row.contactEmail, sortable: true,
      },
      {
        name: 'Number Of Units', align: 'right', label: 'Number of Units', field: (row) => row.numberOfUnits,
      },
      {
        name: 'Notes',
        label: 'Notes',
        align: 'left',
        field: (row) => row.notes,
        classes: 'ellipsis',
        style: 'max-width: 200px',
        headerStyle: 'max-width: 200px',
        sortable: true,
      },
    ];

    const complexes = ref();
    const pagination = ref({
      rowsPerPage: 10,
      rowsNumber: -1,
      page: 1,
      sortBy: 'desc',
      descending: false,
    });

    const preProcessComplexes = () => {
      for (let i = 0; i < complexes.value.length; i += 1) {
        complexes.value[i].unitToggle = false;
        complexes.value[i].unitModels = [];
      }
    };

    const fetchComplexes = async (
      curPage = 1, curRowsPerPage = 10, curDescending, curSortBy, curFilter,
    ) => {
      $q.loading.show();
      try {
        const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Complexes`;
        const response = await axios.get(url, {
          params: {
            page: curPage,
            rowsPerPage: curRowsPerPage,
            descending: curDescending,
            sortBy: curSortBy,
            filter: curFilter,
            isShowingRetired: isShowingRetired.value,
          },
        });
        const { data } = response;
        complexes.value = data.data;
        pagination.value.rowsNumber = data.rowsNumber;
        preProcessComplexes();
      } catch (error) {
        console.log(error);
      }
      $q.loading.hide();
    };

    const fetchUnitsByComplexId = async (props) => {
      try {
        if (props.row.unitToggle) {
          const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Units`;
          const response = await axios.get(url, {
            params: {
              // page: props.row.page,
              // rowsPerPage: props.row.rowsPerPage,
              isShowingRetired: isShowingRetired.value,
              complexId: props.row.complexId,
            },
          });
          const { data } = response;
          const currentIndex = Object.keys(complexes.value).find(
            (key) => complexes.value[key].complexId === props.row.complexId,
          );
          complexes.value[currentIndex].unitModels = data;
        }
      } catch (error) {
        console.log(error);
      }
    };

    const patchRow = async (id, rowType, data) => {
      try {
        const path = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/${rowType}/${id}`;
        const response = await axios.patch(path, data);

        return response;
      } catch (error) {
        console.log(error);
        return error.response;
      }
    };

    const updateRowRetirement = async (id, rowType, isRetired) => {
      try {
        const data = [
          {
            op: 'replace',
            path: 'retired',
            value: isRetired,
          },
        ];
        const response = await patchRow(id, rowType, data);
        notifyResult(response.status, response);
        if (response.status === 200 || response.status === 201) {
          // updates unit or complex table row with response
          if (rowType === 'Units') {
            for (let i = 0; i < complexes.value.length; i += 1) {
              if (complexes.value[i].complexId === response.data.value.complexId) {
                for (let j = 0; j < complexes.value[i].unitModels.length; j += 1) {
                  if (complexes.value[i].unitModels[j].unitId === response.data.value.unitId) {
                    if (isShowingRetired.value) {
                      complexes.value[i].unitModels[j] = response.data.value;
                    } else {
                      complexes.value[i].unitModels.splice(j, 1);
                      complexes.value[i].numberOfUnits -= 1;
                    }

                    return;
                  }
                }
              }
            }
          } else if (rowType === 'Complexes') {
            for (let i = 0; i < complexes.value.length; i += 1) {
              if (complexes.value[i].complexId === id) {
                if (!isShowingRetired.value && isRetired) {
                  complexes.value.splice(i, 1);
                  pagination.value.rowsNumber -= 1;
                } else {
                  complexes.value[i] = response.data.value;
                  complexes.value[i].unitToggle = false;
                  complexes.value[i].unitModels = [];
                }

                return;
              }
            }
          }
        }
      } catch (error) {
        console.log(error);
      }
    };

    const retireRow = async (id, rowType) => {
      await updateRowRetirement(id, rowType, true);
    };

    const unRetireRow = async (id, rowType) => {
      await updateRowRetirement(id, rowType, false);
    };

    const onRequest = async (props) => {
      $q.loading.show();
      const page = props.pagination?.page ?? pagination.value.page;
      const rowsPerPage = props.pagination?.rowsPerPage ?? pagination.value.rowsPerPage;
      const sortBy = props.pagination?.sortBy ?? pagination.value.sortBy;
      const descending = props.pagination?.descending ?? pagination.value.descending;

      await fetchComplexes(
        page,
        rowsPerPage,
        descending,
        sortBy,
        null,
      );
      pagination.value.page = page;
      pagination.value.rowsPerPage = rowsPerPage;
      pagination.value.sortBy = sortBy;
      pagination.value.descending = descending;

      $q.loading.hide();
    };

    const showRetired = async () => {
      isShowingRetired.value = true;
      await fetchComplexes(pagination.value.page, pagination.value.rowsPerPage);
    };

    const hideRetired = async () => {
      isShowingRetired.value = false;
      await fetchComplexes(pagination.value.page, pagination.value.rowsPerPage);
    };

    await fetchComplexes();

    const updateComplexRow = (addedUnit = false) => {
      for (let i = 0; i < complexes.value.length; i += 1) {
        if (complexes.value[i].complexId === currentComplexId.value) {
          complexDetails.value.unitModels = complexes.value[i].unitModels;
          complexDetails.value.unitToggle = complexes.value[i].unitToggle;
          complexDetails.value.numberOfUnits = complexes.value[i].numberOfUnits;
          if (addedUnit) {
            complexDetails.value.numberOfUnits += 1;
          }
          complexes.value[i] = JSON.parse(JSON.stringify(complexDetails.value));

          return;
        }
      }
      complexDetails.value.unitModels = [];
      complexDetails.value.unitToggle = false;
      complexes.value.push(complexDetails.value);
    };

    const submitComplexForm = async () => {
      $q.loading.show();
      const response = await complexForm.value.onSubmit();
      if (response.status === 200 || response.status === 201) {
        currentComplexId.value = response.data.data.complexId;
        complexDetails.value = response.data.data;
        updateComplexRow();
        complexEditModal.value.hide();
      }
      $q.loading.hide();
    };

    const updateComplexState = (updateComplexId) => {
      currentComplexId.value = updateComplexId;
    };

    const updateUnitRow = () => {
      const currentIndex = Object.keys(complexes.value).find(
        (key) => complexes.value[key].complexId === currentComplexId.value,
      );
      for (let i = 0; i < complexes.value[currentIndex].unitModels.length; i += 1) {
        if (complexes.value[currentIndex].unitModels[i].unitId === currentUnitId.value) {
          complexes.value[currentIndex].unitModels[i] = JSON.parse(
            JSON.stringify(unitDetails.value),
          );

          return;
        }
      }
      complexes.value[currentIndex].unitModels.push(
        JSON.parse(JSON.stringify(unitDetails.value)),
      );
    };
    const submitUnitForm = async () => {
      $q.loading.show();
      const response = await unitForm.value.onSubmit();
      if (response === 200 || response === 201) {
        await fetchComplex(currentComplexId.value, isShowingRetired.value);
        updateComplexRow(true);
        await fetchUnit(currentUnitId.value);
        updateUnitRow();
        unitEditModal.value.hide();
      }
      $q.loading.hide();
    };
    const updateUnitState = (updateUnitId) => {
      currentUnitId.value = updateUnitId;
    };

    const exportUnits = async () => {
      try {
        const result = await getUnitsExport();
        if (result.status) {
          notifyResult(result.status, result);
        } else {
          createDownloadableFile(result, 'units_export.csv', 'text/csv;charset=utf-8;');
        }
      } catch (error) {
        window.alert(error);
      }
    };

    return {
      confirmRetire: ref(false),
      complexEditModal,
      complexForm,
      currentComplexId,
      exportUnits,
      hasRoles,
      unitForm,
      submitComplexForm,
      submitUnitForm,
      updateComplexState,
      updateUnitState,
      isAddComplexModal: ref(false),
      isAddUnitModal: ref(false),
      isRa,
      isShowingRetired,
      hideRetired,
      showRetired,
      retireRow,
      unRetireRow,
      onRequest,
      fetchUnitsByComplexId,
      resetCurrentUnit,
      unitEditModal,
      unitTableLoading: ref(false),
      visibleComplexColumns: ref([
        'Complex Name',
        'Number Of Units',
        'Contact Name',
        'Contact Email',
        'Contact Phone',
        'Notes',
      ]),
      visibleUnitColumns: ref([
        'Unit Name',
        'Capacity',
        'DSS Accessible',
        'WiFi Name',
        'WiFi Password',
        'Room Rate Class',
        'Notes',
      ]),
      unitColumns,
      complexColumns,
      complexes,
      pagination,
      retired,
    };
  },
  components: {
    SubmitComplex,
    SubmitUnit,
  },
};
</script>
